import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import type { NavigationProps, PaginationProps } from '../pagination/types';
import { useSlider } from '../SliderContext';
import { Arrow } from './arrow/Arrow';
import { MOBILE_BREAKPOINT, NARROW_BREAKPOINT } from './constants';
import {
  BottomCenterLayout,
  BottomLeftLayout,
  BottomRightLayout,
  OtherLayout
} from './layouts';
import { Pagination } from './pagination/Pagination';
import { ArrowDirection, isLayoutPositionType } from './types';
import { useElementSize } from './utils';

const CombinedNavigationLayouts = {
  bottomCenter: BottomCenterLayout,
  bottomLeft: BottomLeftLayout,
  center: OtherLayout,
  bottomRight: BottomRightLayout,
  topLeft: OtherLayout,
  topRight: OtherLayout
} as const;

type AdditionalPaginationSettingsType = Partial<{
  maxPaginationElementsCount: number;
  activePaginationElementViewIndex: number;
  customMargin: string;
}>;

const additionalPaginationSettings: Record<
  string,
  AdditionalPaginationSettingsType
> = {
  bullets: {
    maxPaginationElementsCount: 6,
    activePaginationElementViewIndex: 2
  },
  numbers: {
    customMargin: '12px 16px'
  },
  lines: {
    maxPaginationElementsCount: 3,
    activePaginationElementViewIndex: 1,
    customMargin: '5px 16px'
  }
};

function getAdditionalPaginationSettings(
  paginationType: string,
  position: string
) {
  if (paginationType === 'numbers' && position === 'bottomCenter') {
    return {
      customMargin: '12px 8px'
    };
  }

  return additionalPaginationSettings[paginationType];
}

interface CombinedNavigationProps {
  navigationSettings: NavigationProps;
  paginationSettings: PaginationProps;
  shouldHaveArrows: boolean;
  shouldHavePagination: boolean;
  onPaginationHeightChange: (paginationHeight: number) => void;
}

export function CombinedNavigation({
  navigationSettings,
  paginationSettings,
  shouldHaveArrows,
  shouldHavePagination,
  onPaginationHeightChange
}: CombinedNavigationProps) {
  const {
    sliderId,
    prevSlide,
    showPrev,
    showNext,
    nextSlide,
    navigationClassName,
    position,
    customIcons: { prev, next }
  } = navigationSettings;

  const { type: paginationType } = paginationSettings;

  const { appClassnamePrefix } = useSlider();

  const [navigationRef, { width: navigationWidth }] = useElementSize();
  const [paginationRef, { scrollHeight: paginationHeight }] = useElementSize();
  const [navigationContainerHeight, setNavigationContainerHeight] =
    useState<number>();

  const finalNavigationClassName =
    navigationClassName || `${appClassnamePrefix}-combined-navigation`;
  const bottomPaginationHeight = position.startsWith('bottom')
    ? (navigationContainerHeight ?? paginationHeight)
    : paginationHeight;

  const isNavigationNarrow = navigationWidth < NARROW_BREAKPOINT;
  const isMobile = navigationWidth < MOBILE_BREAKPOINT;

  const normalizedPosition = isLayoutPositionType(position)
    ? position
    : 'center';

  const NavigationLayout = CombinedNavigationLayouts[normalizedPosition];

  useEffect(() => {
    onPaginationHeightChange(bottomPaginationHeight);
  }, [bottomPaginationHeight]);

  const finalPaginationSettings = useMemo(
    () => ({
      ...paginationSettings,
      ...getAdditionalPaginationSettings(paginationType, position),
      isNarrow: isNavigationNarrow,
      shouldUseNewAppearance: true
    }),
    [paginationSettings, paginationType, isNavigationNarrow]
  );

  return (
    <StyledCombinedNavigation
      _appClassPrefix={appClassnamePrefix}
      _navigationClassName={navigationClassName}
      ref={navigationRef}
      aria-label="Slider Controls"
    >
      <NavigationLayout
        position={position}
        isMobile={isMobile}
        onSizeChange={({ height }) => {
          setNavigationContainerHeight(height);
        }}
        prevArrow={
          shouldHaveArrows ? (
            <Arrow
              sliderId={sliderId}
              navigationClassName={finalNavigationClassName}
              customIcon={prev}
              position={position}
              withPagination={shouldHavePagination}
              direction={ArrowDirection.Prev}
              onClick={() => prevSlide()}
              isEnabled={showPrev()}
            />
          ) : null
        }
        nextArrow={
          shouldHaveArrows ? (
            <Arrow
              sliderId={sliderId}
              navigationClassName={finalNavigationClassName}
              customIcon={next}
              position={position}
              withPagination={shouldHavePagination}
              direction={ArrowDirection.Next}
              onClick={() => nextSlide()}
              isEnabled={showNext()}
            />
          ) : null
        }
        pagination={
          shouldHavePagination ? (
            <Pagination
              settings={finalPaginationSettings}
              position={position}
              ref={paginationRef}
            />
          ) : null
        }
      />
    </StyledCombinedNavigation>
  );
}

interface StyledCombinedNavigationProps {
  _appClassPrefix: string;
  _navigationClassName: string;
}

const StyledCombinedNavigation = styled.div.attrs<StyledCombinedNavigationProps>(
  ({ _appClassPrefix, _navigationClassName }) => ({
    className: classNames(
      _navigationClassName,
      `${_appClassPrefix}-combined-navigation`
    )
  })
)`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: center;
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
`;
